import { useLocation } from 'react-router-dom';

/**
 * Checks if the current window is truly embedded as an iframe or popup window,
 * independent of any URL marking.
 */
export const isEmbeddedWindow = () => {
    if (typeof window === 'undefined') return false;
    return window.self !== window.top || Boolean(window.opener);
};

/**
 * Checks if the request is explicitly marked as dashboard preview via query parameter.
 */
export const hasDashboardSourceParam = (location) => {
    if (typeof window === 'undefined') return false;
    const searchParams = new URLSearchParams(location?.search || window.location?.search || '');
    return searchParams.get('source') === 'dashboard';
};

/**
 * Checks if the current window is embedded inside an iframe, popup opener,
 * or explicitly marked as dashboard preview via query parameter.
 */
export const isLivePreviewEmbedded = (location) => {
    return isEmbeddedWindow() || hasDashboardSourceParam(location);
};

/**
 * Checks if the given location is on the live preview route.
 */
export const isLivePreviewRoute = (location) => {
    const pathname = location?.pathname || (typeof window !== 'undefined' ? window.location?.pathname : '');
    return Boolean(pathname && pathname.startsWith('/__live_preview'));
};

/**
 * Checks if the current view is an active live preview (both on preview route and embedded).
 */
export const isLivePreview = (location) => {
    return isLivePreviewRoute(location) && isLivePreviewEmbedded(location);
};

/**
 * Custom React Hook that returns true if the current route is an active embedded live preview.
 */
export const useIsPreview = () => {
    const location = useLocation();
    return isLivePreview(location);
};

/**
 * Checks if the current view is any statically detectable form of preview: the
 * dedicated preview route or a URL explicitly marked as dashboard source (the
 * dashboard appends the parameter to every preview URL). This check is synchronous,
 * so preview-only behaviour (e.g. suppressing the consent modal and tracking scripts)
 * can apply before any handshake. Dashboard contexts without URL marking are covered
 * by the live preview connection state (livePreviewStore) once the handshake confirms.
 */
export const isPreviewContext = (location) => {
    return isLivePreviewRoute(location) || hasDashboardSourceParam(location);
};

/**
 * Custom React Hook that returns true if the current view is any statically detectable
 * form of preview (see isPreviewContext).
 */
export const useIsPreviewContext = () => {
    const location = useLocation();
    return isPreviewContext(location);
};
